import { EventType } from '@ag-ui/core';
import type { BaseEvent } from '@ag-ui/core';
import type { Attachment } from '@langbridge-builder/design-system';

export type ChatPhase = 'idle' | 'running' | 'interrupted' | 'done' | 'aborted' | 'error';

/**
 * Live activity derived from the AG-UI event stream, surfaced by the view as an
 * ActivityIndicator while the assistant works. Literals mirror the design-system
 * `ActivityStatus` (kept in sync intentionally; the state layer stays DS-free).
 */
export type ActivityStatus =
  | 'starting'
  | 'thinking'
  | 'step'
  | 'tool'
  | 'toolResult'
  | 'writing';

export interface Activity {
  status: ActivityStatus;
  /** Variable step/tool name for label interpolation. */
  detail?: string;
}

export interface Citation {
  num: number;
  title: string;
  url: string;
  span: string;
  endIndex: number;
}

/**
 * A deep-agent assistant turn interleaves narration with process (subagent /
 * tool / step) inside ONE bubble: "먼저 찾아볼게요" → ⟳ tool → "정리하면…". We
 * model the assistant message as an ordered list of blocks so the view can
 * render text and inline activity (live spinner → collapsed ✓ trace) in order.
 */
export interface TextBlock {
  kind: 'text';
  text: string;
}

export interface ActivityBlock {
  kind: 'activity';
  status: ActivityStatus;
  detail?: string;
  /** 'active' shows a live spinner; 'done' collapses to a muted ✓ trace line. */
  state: 'active' | 'done';
  /** ms epoch the block became active (for elapsed-time trace). */
  startedAt?: number;
  /** Elapsed seconds, stamped when the block settles to 'done'. */
  durationSec?: number;
  /** Sub-agent nesting level (0 = top). Drives ActivityTrace's indented rails. */
  depth?: number;
  /**
   * Streamed sub-event chunks accumulated while the block is live — reasoning
   * prose (REASONING/THINKING content) or tool-call argument JSON. Surfaced in a
   * foldable panel so the wait shows real progress instead of a bare spinner.
   */
  chunks?: string;
  /** How to render the chunk buffer: 'reasoning' prose vs 'args' JSON (mono). */
  chunkKind?: 'reasoning' | 'args';
}

/**
 * A HIL pause rendered inline in the assistant turn, at the position it
 * occurred, so the resumed continuation streams BELOW the approval card
 * (not above it). Carries its own actions so the card persists per-message.
 */
export interface InterruptBlock {
  kind: 'interrupt';
  id: string;
  actions: InterruptAction[];
}

/**
 * A file surfaced by the deep-agent `show_file` semantic tool, rendered inline in
 * the turn as a viewer card (path/title header + content). Arrives as a
 * `CUSTOM name:'show_file'` event with `{ path, title?, content }`.
 */
export interface FileBlock {
  kind: 'file';
  path: string;
  title?: string;
  content: string;
  /**
   * Absolute local path (or file:// URI) of the file on disk, when the backend
   * knows it (desktop / local filesystem). Enables "reveal in file explorer".
   */
  uri?: string;
}

/**
 * Positional marker for a turn's A2UI (generative UI) surface. The surface itself
 * (its whole surfaceId history) lives in ChatMessage.a2ui for the per-turn
 * MessageProcessor; this block only records WHERE in the turn's block sequence
 * the surface first appeared, so the view renders it inline in chronological
 * order (before/after the surrounding text) instead of pinned to the turn bottom.
 */
export interface A2uiBlock {
  kind: 'a2ui';
}

export type MessageBlock = TextBlock | ActivityBlock | InterruptBlock | FileBlock | A2uiBlock;

export interface ChatMessage {
  role: 'user' | 'assistant';
  /** Flattened text (user turn, and aggregate of assistant text blocks). */
  text: string;
  streaming?: boolean;
  cites?: Citation[];
  /** Assistant only: ordered content blocks (source of truth for rendering). */
  blocks?: MessageBlock[];
  /**
   * Assistant only: A2UI (generative UI) server_to_client messages emitted during
   * THIS turn. Pinned to the message so the surface renders inline at its position
   * in the transcript (like a bubble) instead of floating at the bottom.
   */
  a2ui?: Record<string, unknown>[];
  /**
   * Assistant only: this turn's A2UI surface has been interacted with (an action
   * was dispatched from it) and is now frozen read-only. The view keeps it visible
   * as a record but disables interaction so it can't re-fire.
   */
  a2uiConsumed?: boolean;
  /** User only: multimodal attachments sent with this turn (rendered as chips). */
  attachments?: Attachment[];
}

/** step/tool leave a persistent ✓ trace; starting/thinking/writing are transient. */
function isPersistent(status: ActivityStatus): boolean {
  return status === 'step' || status === 'tool';
}

/**
 * Human-in-the-loop (HIL), AG-UI standard. deep_agent pauses before a guarded
 * tool call by FINISHING the run with
 * `RUN_FINISHED { outcome: { type:'interrupt', interrupts: Interrupt[] } }`
 * (one flattened action per Interrupt, id `${lgId}#${index}`). The user answers
 * with a decision per action, sent back as a fresh run carrying the top-level
 * `resume: ResumeEntry[]` ({ interruptId, status, payload }) to resume the graph.
 */
export type InterruptDecisionType = 'approve' | 'edit' | 'reject';

export interface InterruptAction {
  /**
   * AG-UI standard interrupt id for this action (`${lgInterruptId}#${index}`).
   * Echoed back as `ResumeEntry.interruptId` so the backend restores order.
   */
  id?: string;
  name: string;
  args: Record<string, unknown>;
  /** Decisions the backend allows for this action (buttons to offer). */
  allowedDecisions: InterruptDecisionType[];
  /** Human-readable one-liner (backend-normalized). */
  summary?: string;
}

export interface PendingInterrupt {
  id: string;
  actions: InterruptAction[];
}

export type InterruptDecision =
  | { type: 'approve' }
  | { type: 'edit'; editedAction: { name: string; args: Record<string, unknown> } }
  | { type: 'reject'; message?: string };

export interface ChatState {
  phase: ChatPhase;
  messages: ChatMessage[];
  error?: string;
  /** Current live activity while running; null when idle/done/aborted/error. */
  activity: Activity | null;
  /** Pending HIL interrupt awaiting a human decision; null otherwise. */
  interrupt: PendingInterrupt | null;
  /** Current sub-agent nesting depth (0 = top); stamped onto new activity blocks. */
  subagentDepth: number;
}

export const initialChatState: ChatState = {
  phase: 'idle',
  messages: [],
  activity: null,
  interrupt: null,
  subagentDepth: 0,
};

export type ChatAction =
  | { kind: 'start'; text: string; attachments?: Attachment[]; fromAction?: boolean; retry?: boolean }
  | { kind: 'event'; event: BaseEvent }
  | { kind: 'resume' }
  | { kind: 'finish' }
  | { kind: 'aborted' }
  | { kind: 'error'; msg: string }
  | { kind: 'hydrate'; messages: ChatMessage[] }
  | { kind: 'reset' };

function lastAssistantIndex(messages: ChatMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === 'assistant') return i;
  }
  return -1;
}

/** A2UI messages carry their surfaceId inside the single top-level payload object. */
function a2uiSurfaceId(msg: Record<string, unknown>): string | undefined {
  for (const v of Object.values(msg)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const id = (v as { surfaceId?: unknown }).surfaceId;
      if (typeof id === 'string' && id) return id;
    }
  }
  return undefined;
}

/** True if this A2UI message CREATES a surface (vs. updating an existing one). */
function isCreateSurface(msg: Record<string, unknown>): boolean {
  return !!msg && typeof msg === 'object' && 'createSurface' in msg;
}

/**
 * The turn that OWNS surface `sid` — the latest assistant turn whose A2UI history
 * contains a createSurface for it. Non-create updates route here so the owning
 * turn's renderer stays self-contained (createSurface + its updates together, no
 * orphan "Surface not found"). -1 when no turn has created this surface.
 */
function surfaceOwnerIndex(messages: ChatMessage[], sid: string): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const a2ui = messages[i]?.a2ui;
    if (a2ui?.some((msg) => isCreateSurface(msg) && a2uiSurfaceId(msg) === sid)) return i;
  }
  return -1;
}

/**
 * Attach incoming A2UI messages, keeping each surface anchored to its OWN turn so
 * multiple surfaces render as sequential inline cards (the view freezes all but
 * the newest live one). Two rules:
 *   - createSurface → a NEW independent surface at the CURRENT turn. It never
 *     pulls prior same-id surfaces down, so distinct forms coexist even when the
 *     backend reuses one surfaceId (e.g. "main"). Each turn is its own renderer.
 *   - updateComponents / other → route to the surface's OWNING turn (the latest
 *     one that created this sid) and re-activate it, so it updates in place and
 *     the owning turn stays self-contained. Orphan updates fall back to current.
 */
function attachA2ui(messages: ChatMessage[], incoming: Record<string, unknown>[]): ChatMessage[] {
  const curIdx = lastAssistantIndex(messages);
  if (curIdx < 0) return messages;
  const out = messages.slice();
  for (const msg of incoming) {
    const create = isCreateSurface(msg);
    const sid = a2uiSurfaceId(msg);
    const owner = !create && sid ? surfaceOwnerIndex(out, sid) : -1;
    const idx = owner >= 0 ? owner : curIdx;
    const m = out[idx]!;
    out[idx] = {
      ...m,
      a2ui: [...(m.a2ui ?? []), msg],
      // Drop a one-time positional marker into the block stream (settling any
      // live spinner first, like text/file) so the surface renders where it
      // first appeared in the turn, not at the bottom.
      blocks: ensureA2uiBlock(m.blocks),
      // An update means the owning surface is live again (multi-step forms).
      ...(owner >= 0 ? { a2uiConsumed: false } : null),
    };
  }
  return out;
}

/** Ensure the turn carries exactly one A2UI position marker, at the current spot. */
function ensureA2uiBlock(blocks: MessageBlock[] = []): MessageBlock[] {
  if (blocks.some((b) => b.kind === 'a2ui')) return blocks;
  return [...closeActive(blocks), { kind: 'a2ui' }];
}

/**
 * Freeze the latest live A2UI surface: an action was dispatched from it, so mark
 * it consumed (read-only) to stop it re-firing. A fresh surface in the response
 * becomes the new live one; older surfaces are already superseded.
 */
function consumeLastSurface(messages: ChatMessage[]): ChatMessage[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role === 'assistant' && m.a2ui?.length && !m.a2uiConsumed) {
      const out = messages.slice();
      out[i] = { ...m, a2uiConsumed: true };
      return out;
    }
  }
  return messages;
}

function finalize(
  state: ChatState,
  phase: ChatPhase,
  error?: string,
  clearInterrupt = true,
): ChatState {
  const messages = state.messages.map((m) =>
    m.role === 'assistant'
      ? { ...m, streaming: false, blocks: m.blocks ? finalizeBlocks(m.blocks) : m.blocks }
      : m,
  );
  // On a normal 'done' we keep the resolved interrupt so its decision-trace card
  // stays mounted (cleared on the next 'start'/'reset'); abort/error clear it.
  return {
    ...state,
    phase,
    messages,
    error,
    activity: null,
    subagentDepth: 0,
    interrupt: clearInterrupt ? null : state.interrupt,
  };
}

/** Trim a qualified tool name (`node__uuid.tavily_search`) to its leaf. */
function toolLeaf(name: unknown): string | undefined {
  if (typeof name !== 'string' || !name) return undefined;
  return name.split('.').pop() || name;
}

/**
 * Backend step/tool names are engineering identifiers (`tavily_search`,
 * `web_fetch`, `write_todos`). Map the well-known ones to friendly Korean and
 * prettify the rest (`some_tool` → `some tool`) so the trace reads for humans.
 */
const FRIENDLY_NAMES: Record<string, string> = {
  tavily_search: '웹 검색',
  web_search: '웹 검색',
  internet_search: '웹 검색',
  search: '웹 검색',
  web_fetch: '웹페이지 확인',
  fetch: '웹페이지 확인',
  browse: '웹 탐색',
  task: '하위 작업 실행',
  write_todos: '할 일 정리',
  todo: '할 일 정리',
  write_file: '파일 작성',
  read_file: '파일 읽기',
  edit_file: '파일 수정',
  ls: '파일 목록 확인',
  glob: '파일 검색',
  grep: '내용 검색',
  python: '코드 실행',
  execute: '코드 실행',
  bash: '명령 실행',
};

/** Internal "the model is generating" steps — shown as transient thinking, not a tool trace. */
const MODEL_STEPS = new Set(['model_call', 'model', 'llm', 'call_model', 'generate', 'agent']);

function isModelStep(name: unknown): boolean {
  return typeof name === 'string' && MODEL_STEPS.has(name.toLowerCase());
}

function friendlyName(name: unknown): string | undefined {
  if (typeof name !== 'string' || !name) return undefined;
  const key = name.toLowerCase();
  return FRIENDLY_NAMES[key] ?? key.replace(/_(tool|node)$/, '').replace(/_/g, ' ');
}

// ---- Block transforms (operate on an assistant message's block list) ----

/** Wall clock (ms). Isolated so tests/timing stay easy to reason about. */
function now(): number {
  return Date.now();
}

/** Elapsed seconds since a block became active (undefined if never stamped). */
function elapsedSec(startedAt?: number): number | undefined {
  if (startedAt == null) return undefined;
  return Math.max(0, (now() - startedAt) / 1000);
}

/** Settle the trailing active block: persistent→done (kept as trace), transient→dropped. */
function closeActive(blocks: MessageBlock[]): MessageBlock[] {
  const out = blocks.slice();
  const last = out[out.length - 1];
  if (last && last.kind === 'activity' && last.state === 'active') {
    // Keep persistent traces (step/tool) and any block that captured chunks
    // (reasoning/args) so they collapse into a re-openable done trace; drop
    // bare transient thinking/writing spinners.
    if (isPersistent(last.status) || (last.chunks && last.chunks.length > 0)) {
      out[out.length - 1] = { ...last, state: 'done', durationSec: elapsedSec(last.startedAt) };
    } else out.pop();
  }
  return out;
}

/** Start/refresh a live activity block, settling any different trailing one first. */
function pushActivity(
  blocks: MessageBlock[],
  status: ActivityStatus,
  detail?: string,
  depth = 0,
): MessageBlock[] {
  let out = blocks.slice();
  const last = out[out.length - 1];
  if (last && last.kind === 'activity' && last.state === 'active') {
    if (last.status === status && (last.depth ?? 0) === depth) {
      out[out.length - 1] = { ...last, detail: detail ?? last.detail };
      return out;
    }
    out = closeActive(out);
  }
  const block: ActivityBlock = { kind: 'activity', status, detail, state: 'active', startedAt: now() };
  if (depth > 0) block.depth = depth;
  out.push(block);
  return out;
}

/** Append streamed text, settling any trailing live activity into the transcript. */
function appendText(blocks: MessageBlock[], delta: string): MessageBlock[] {
  const out = closeActive(blocks);
  const last = out[out.length - 1];
  if (last && last.kind === 'text') out[out.length - 1] = { ...last, text: last.text + delta };
  else out.push({ kind: 'text', text: delta });
  return out;
}

/** Settle every trailing live block when the run ends. */
function finalizeBlocks(blocks: MessageBlock[]): MessageBlock[] {
  let out = blocks;
  // closeActive settles one trailing active block; loop drains any transients.
  while (out[out.length - 1]?.kind === 'activity' && (out[out.length - 1] as ActivityBlock).state === 'active') {
    out = closeActive(out);
  }
  return out;
}

/** Apply a block transform to the current (last) assistant message. */
function withBlocks(state: ChatState, fn: (blocks: MessageBlock[]) => MessageBlock[]): ChatState {
  const idx = lastAssistantIndex(state.messages);
  if (idx < 0) return state;
  const messages = state.messages.slice();
  const cur = messages[idx]!;
  messages[idx] = { ...cur, blocks: fn(cur.blocks ?? []) };
  return { ...state, messages };
}

/** Record live activity (drives the standalone pre-content indicator) + block. */
function activityEvent(
  state: ChatState,
  status: ActivityStatus,
  detail?: string,
  depth = state.subagentDepth,
): ChatState {
  const next = withBlocks(state, (b) => pushActivity(b, status, detail, depth));
  return { ...next, activity: { status, detail } };
}

/** Open/refresh the active block of `status`, then append a streamed chunk to it. */
function appendChunk(
  blocks: MessageBlock[],
  status: ActivityStatus,
  chunkKind: 'reasoning' | 'args',
  delta: string,
  detail: string | undefined,
  depth: number,
): MessageBlock[] {
  const out = pushActivity(blocks, status, detail, depth);
  const last = out[out.length - 1] as ActivityBlock;
  out[out.length - 1] = { ...last, chunks: (last.chunks ?? '') + delta, chunkKind };
  return out;
}

/** Accumulate a streamed sub-event chunk (reasoning/args) onto the live block. */
function chunkEvent(
  state: ChatState,
  status: ActivityStatus,
  chunkKind: 'reasoning' | 'args',
  delta: string,
  detail?: string,
  depth = state.subagentDepth,
): ChatState {
  if (!delta) return state;
  const next = withBlocks(state, (b) => appendChunk(b, status, chunkKind, delta, detail, depth));
  return { ...next, activity: { status, detail } };
}

/** Settle the trailing live activity block (keeps persistent/chunked ones as ✓ traces). */
function settleEvent(state: ChatState): ChatState {
  return withBlocks(state, closeActive);
}

/**
 * Capture a finished tool's result into its live tool block as a foldable body —
 * but ONLY when the block streamed no argument chunks (i.e. the model didn't emit
 * TOOL_CALL_ARGS deltas). Without this, a tool that runs without streaming args
 * (e.g. some write_file calls) shows a bare spinner with nothing to reveal; the
 * result content becomes the disclosure body so the trace shows what the tool did.
 * When args DID stream, they already convey the action, so the result is skipped.
 */
function captureToolResult(state: ChatState, content: unknown): ChatState {
  if (content == null || content === '') return state;
  return withBlocks(state, (blocks) => {
    const out = blocks.slice();
    const last = out[out.length - 1];
    if (
      last &&
      last.kind === 'activity' &&
      last.state === 'active' &&
      last.status === 'tool' &&
      !(last.chunks && last.chunks.length > 0)
    ) {
      const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
      out[out.length - 1] = { ...last, chunks: text, chunkKind: 'args' };
    }
    return out;
  });
}

/**
 * Translate a backend `CUSTOM name:'subagent'` event carrying a `kind` tag into
 * inline activity — surfacing a delegated sub-agent's work (its steps, streamed
 * answer, and tool calls) nested under the parent turn at `value.depth`:
 *   - kind:'node'    → a step/thinking block (model_call → thinking, else a step).
 *   - kind:'message' → the sub-agent's answer prose, streamed into a foldable block.
 *   - kind:'tool'    → a tool block (running: label + streamed args; finished: result).
 * (The legacy bare `{ depth }` marker — still emitted by MockChatClient — is handled
 * by the caller, which stamps `subagentDepth` onto the parent's own events instead.)
 */
function applySubagentEvent(state: ChatState, v: Record<string, unknown>): ChatState {
  const depth = Math.max(1, Number(v.depth) || 1);
  const kind = v.kind as string | undefined;
  const status = v.status as string | undefined;

  if (kind === 'node') {
    if (status === 'finished') return settleEvent(state);
    return isModelStep(v.name)
      ? activityEvent(state, 'thinking', undefined, depth)
      : activityEvent(state, 'step', friendlyName(v.name), depth);
  }

  if (kind === 'message') {
    const delta = typeof v.delta === 'string' ? v.delta : '';
    return chunkEvent(state, 'thinking', 'reasoning', delta, '하위 에이전트 응답', depth);
  }

  if (kind === 'tool') {
    const leaf = toolLeaf(v.name);
    if (status === 'finished') return settleEvent(captureToolResult(state, v.result));
    let s = activityEvent(state, 'tool', friendlyName(leaf), depth);
    if (v.args !== undefined) {
      const args = typeof v.args === 'string' ? v.args : JSON.stringify(v.args, null, 2);
      s = chunkEvent(s, 'tool', 'args', args, friendlyName(leaf), depth);
    }
    return s;
  }

  return state;
}

function applyAnnotation(
  state: ChatState,
  value: { url?: string; title?: string; startIndex?: number; endIndex?: number },
): ChatState {
  const { url = '', title = '', startIndex = 0, endIndex = 0 } = value;
  const idx = lastAssistantIndex(state.messages);
  if (idx < 0) return state;
  const messages = state.messages.slice();
  const cur = messages[idx]!;
  const prev = cur.cites ?? [];
  const cite: Citation = { num: prev.length + 1, title, url, span: `[${startIndex},${endIndex}]`, endIndex };
  messages[idx] = { ...cur, cites: [...prev, cite] };
  return { ...state, messages };
}

/**
 * Append a `show_file` viewer block to the current assistant turn, in order
 * (settling any live spinner first, like text). If no assistant message exists
 * yet (file surfaced before any narration), open one so the card still shows.
 */
function appendFileBlock(state: ChatState, block: FileBlock): ChatState {
  const idx = lastAssistantIndex(state.messages);
  if (idx < 0) {
    return {
      ...state,
      messages: [
        ...state.messages,
        { role: 'assistant', text: '', streaming: state.phase === 'running', cites: [], blocks: [block] },
      ],
    };
  }
  const messages = state.messages.slice();
  const cur = messages[idx]!;
  messages[idx] = { ...cur, blocks: [...closeActive(cur.blocks ?? []), block] };
  return { ...state, messages };
}

const DEFAULT_DECISIONS: InterruptDecisionType[] = ['approve', 'edit', 'reject'];

/**
 * Parse AG-UI standard `RUN_FINISHED.outcome.interrupts` (each a flattened
 * Interrupt: `{ id:'${lgId}#${idx}', metadata:{ name, args, allowedDecisions,
 * summary?, lgInterruptId } }`) into our PendingInterrupt. Each action keeps its
 * standard `id` so resume can echo it back as `ResumeEntry.interruptId`.
 */
function parseStandardInterrupt(interrupts: unknown): PendingInterrupt | null {
  const list = Array.isArray(interrupts) ? interrupts : [];
  const actions: InterruptAction[] = [];
  let lgId = 'interrupt';
  for (const it of list) {
    const item = it as { id?: unknown; metadata?: Record<string, any> };
    const md = item?.metadata ?? {};
    lgId = String(md.lgInterruptId ?? String(item?.id ?? 'interrupt').split('#')[0]);
    const allowed =
      Array.isArray(md.allowedDecisions) && md.allowedDecisions.length
        ? (md.allowedDecisions as InterruptDecisionType[])
        : DEFAULT_DECISIONS;
    actions.push({
      id: String(item?.id ?? `${lgId}#${actions.length}`),
      name: String(md.name ?? 'tool'),
      args: (md.args ?? {}) as Record<string, unknown>,
      allowedDecisions: allowed,
      ...(md.summary ? { summary: String(md.summary) } : {}),
    });
  }
  return actions.length ? { id: lgId, actions } : null;
}

/**
 * A settled tool trace whose label matches a gated interrupt action. The backend
 * streams the pending tool call (TOOL_CALL_START/ARGS) then brackets it with a
 * TOOL_CALL_END before finishing with the interrupt — which would otherwise leave
 * a misleading "✓ done" trace above the card. This tool hasn't actually run yet.
 */
function isGatedToolTrace(block: MessageBlock, gatedNames: Set<string>): boolean {
  return (
    block.kind === 'activity' &&
    block.status === 'tool' &&
    block.state === 'done' &&
    block.detail != null &&
    gatedNames.has(block.detail)
  );
}

/** Pause the turn: settle live blocks, drop an inline card marker, stash actions. */
function applyInterrupt(state: ChatState, pending: PendingInterrupt): ChatState {
  const { id, actions } = pending;
  // Names of the gated (awaiting-approval, not-yet-run) tool calls, in our label form.
  const gated = new Set(actions.map((a) => friendlyName(a.name)).filter(Boolean) as string[]);
  // Settle any live block, drop the gated tool's premature ✓ trace(s), then insert
  // an inline interrupt marker so the approval card renders at this point in the
  // turn (the real tool trace streams on resume) and the answer streams below it.
  const settled = withBlocks(state, (b) => {
    let out = closeActive(b);
    while (out.length && isGatedToolTrace(out[out.length - 1]!, gated)) out = out.slice(0, -1);
    return [...out, { kind: 'interrupt', id, actions }];
  });
  return {
    ...settled,
    phase: 'interrupted',
    activity: null,
    interrupt: pending,
  };
}

function applyEvent(state: ChatState, event: BaseEvent): ChatState {
  const e = event as unknown as Record<string, any>;
  switch (event.type) {
    case EventType.RUN_STARTED:
      return { ...activityEvent(state, 'starting'), phase: 'running' };

    // ---- Activity events → live block (+ standalone indicator state) ----
    case EventType.STEP_STARTED:
      // A pure "model is generating" step is thinking, not a labeled tool trace.
      return isModelStep(e.stepName)
        ? activityEvent(state, 'thinking')
        : activityEvent(state, 'step', friendlyName(e.stepName));
    case EventType.TOOL_CALL_START: {
      // Backend names tools like `<node>__<uuid>.tavily_search`; show the leaf.
      const leaf = toolLeaf(e.toolCallName);
      if (isModelStep(leaf)) return activityEvent(state, 'thinking');
      return activityEvent(state, 'tool', friendlyName(leaf) ?? state.activity?.detail);
    }
    case EventType.TOOL_CALL_ARGS:
      // Stream the tool's argument JSON into the live tool block's chunk panel.
      return chunkEvent(state, 'tool', 'args', (e.delta ?? '') as string);
    case EventType.TOOL_CALL_RESULT:
      // Capture the result into the live tool block (only when it streamed no args),
      // then settle it to a ✓ trace whose foldable body shows what the tool did.
      return { ...settleEvent(captureToolResult(state, e.content)), activity: { status: 'toolResult' } };
    case EventType.TOOL_CALL_END:
      // Tool finished: settle its block to a ✓ trace; keep the live label brief.
      return { ...withBlocks(state, closeActive), activity: { status: 'toolResult' } };
    case EventType.THINKING_START:
    case EventType.REASONING_START:
    case EventType.THINKING_TEXT_MESSAGE_START:
    case EventType.REASONING_MESSAGE_START:
      return activityEvent(state, 'thinking');
    case EventType.THINKING_TEXT_MESSAGE_CONTENT:
    case EventType.REASONING_MESSAGE_CONTENT:
      // Stream the model's reasoning prose into the live thinking block's panel.
      return chunkEvent(state, 'thinking', 'reasoning', (e.delta ?? '') as string);
    case EventType.THINKING_TEXT_MESSAGE_END:
    case EventType.REASONING_MESSAGE_END:
    case EventType.THINKING_END:
    case EventType.REASONING_END:
      // Settle reasoning; a chunked block persists as a re-openable done trace.
      return withBlocks(state, closeActive);

    case EventType.TEXT_MESSAGE_START: {
      const idx = lastAssistantIndex(state.messages);
      if (idx < 0 || !state.messages[idx]?.streaming) {
        return {
          ...state,
          activity: { status: 'writing' },
          messages: [...state.messages, { role: 'assistant', text: '', streaming: true, cites: [], blocks: [] }],
        };
      }
      // Stop any running step/tool spinner before narration resumes.
      return { ...withBlocks(state, closeActive), activity: { status: 'writing' } };
    }

    case EventType.TEXT_MESSAGE_CONTENT: {
      const idx = lastAssistantIndex(state.messages);
      if (idx < 0) return state;
      const delta = (e.delta ?? '') as string;
      const messages = state.messages.slice();
      const cur = messages[idx]!;
      messages[idx] = { ...cur, text: cur.text + delta, streaming: true, blocks: appendText(cur.blocks ?? [], delta) };
      return { ...state, messages, activity: { status: 'writing' } };
    }

    case EventType.CUSTOM: {
      if (e.name === 'annotation') return applyAnnotation(state, e.value ?? {});
      if (e.name === 'a2ui') {
        const incoming = (e.value?.messages ?? []) as Record<string, unknown>[];
        if (!incoming.length) return state;
        // Attach to the current (answering) turn, re-anchoring a stateful surface's
        // whole history here on cross-turn updates so it renders where the assistant
        // is replying now (not in place above) — each group stays self-contained.
        return { ...state, messages: attachA2ui(state.messages, incoming) };
      }
      // show_file (deep-agent local file viewer) → inline FileBlock in the turn.
      if (e.name === 'show_file') {
        const v = (e.value ?? {}) as {
          path?: unknown;
          title?: unknown;
          content?: unknown;
          uri?: unknown;
          fileUrl?: unknown;
          absPath?: unknown;
          localPath?: unknown;
        };
        // Absolute on-disk location (desktop/local FS), under any of the field
        // names the backend might use → enables "reveal in file explorer".
        // Prefer the native path (`uri`, no percent-encoding) over the file:// URL
        // (`fileUrl`); both are only present when localFilesystem is active.
        const uri = [v.uri, v.fileUrl, v.absPath, v.localPath].find(
          (x) => typeof x === 'string' && x,
        ) as string | undefined;
        return appendFileBlock(state, {
          kind: 'file',
          path: typeof v.path === 'string' ? v.path : '',
          title: typeof v.title === 'string' ? v.title : undefined,
          content: typeof v.content === 'string' ? v.content : '',
          ...(uri ? { uri } : {}),
        });
      }
      // Sub-agent stream. Two wire shapes:
      //   - kind-tagged ({ kind:'node'|'message'|'tool', depth, ... }) → the real
      //     backend surfacing the delegated agent's work; reduce it to inline blocks.
      //   - bare ({ depth }) → legacy marker (MockChatClient): just set the depth
      //     stamped onto the parent's OWN subsequent events (back-compat).
      if (e.name === 'subagent') {
        const v = (e.value ?? {}) as Record<string, unknown>;
        if (typeof v.kind === 'string') return applySubagentEvent(state, v);
        const depth = Number(v.depth);
        return { ...state, subagentDepth: Number.isFinite(depth) ? Math.max(0, depth) : 0 };
      }
      return state;
    }

    case EventType.RUN_FINISHED: {
      // AG-UI standard HIL: the run finishes with outcome.type==='interrupt' when
      // it paused for a human decision. Settle live blocks + show the approval card
      // (turn stays 'interrupted'; resume is a fresh run). Otherwise finalize —
      // keeping any resolved interrupt so its done-trace card stays mounted.
      const outcome = e.outcome as { type?: string; interrupts?: unknown } | undefined;
      if (outcome?.type === 'interrupt') {
        const pending = parseStandardInterrupt(outcome.interrupts);
        if (pending) return applyInterrupt(state, pending);
      }
      return state.phase === 'interrupted' ? state : finalize(state, 'done', undefined, false);
    }

    case EventType.RUN_ERROR:
      return e.code === 'aborted' ? finalize(state, 'aborted') : finalize(state, 'error', e.message);

    default:
      return state;
  }
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.kind) {
    case 'start': {
      const trimmed = action.retry ? state.messages.slice(0, -2) : state.messages;
      // An action originates from the latest live surface → freeze it on interaction.
      const base = action.fromAction ? consumeLastSurface(trimmed) : trimmed;
      return {
        ...state,
        phase: 'running',
        error: undefined,
        activity: { status: 'starting' },
        interrupt: null,
        messages: [
          ...base,
          {
            role: 'user',
            text: action.text,
            ...(action.attachments?.length ? { attachments: action.attachments } : {}),
          },
          { role: 'assistant', text: '', streaming: true, cites: [], blocks: [] },
        ],
      };
    }
    case 'event':
      return applyEvent(state, action.event);
    case 'resume':
      // Human answered the HIL interrupt: reopen the run (no new user bubble —
      // the checkpointer restores the paused graph state). Keep `interrupt` set
      // so the approval card stays mounted through its submitted → done trace.
      return state.phase === 'interrupted'
        ? { ...state, phase: 'running', error: undefined, activity: { status: 'starting' } }
        : state;
    case 'finish':
      return state.phase === 'running' ? finalize(state, 'done', undefined, false) : state;
    case 'aborted':
      return state.phase === 'running' ? finalize(state, 'aborted') : state;
    case 'error':
      return finalize(state, 'error', action.msg);
    case 'hydrate':
      // Load a restored past-session transcript as the conversation history.
      // Messages are already-shaped ChatMessages; force them non-streaming and
      // reset all live run state (idle, no activity/interrupt). Any A2UI surface
      // is frozen read-only (a2uiConsumed) — the backend thread is gone on reload,
      // so restored surfaces are a record, not re-fireable.
      return {
        ...initialChatState,
        messages: action.messages.map((m) =>
          m.role === 'assistant'
            ? { ...m, streaming: false, ...(m.a2ui?.length ? { a2uiConsumed: true } : {}) }
            : m,
        ),
      };
    case 'reset':
      return { ...initialChatState };
    default:
      return state;
  }
}
